import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Modal } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import "./course.scss";
import closex from "../../assets/images/x.png";
import {
  getCoursesApi,
  getProgramListApi,
  paymentCheckoutURLApi,
} from "../../redux/action/user";
import CenterLoading from "../../component/core/CenterLoading";
import SnackBar from "../../atoms/snackBar";
import { hasFeature, MEMBERSHIP_FEATURE_KEYS } from "../../utils/membershipFeatures";
import { hasActiveMembership } from "../../utils/wordpressAccess";
import {
  formatProgramPrice,
  getMemberDiscountPercent,
  getMemberPrice,
  getNonMemberPrice,
  isIncludedWithMembership,
  isFreeForNonMembers,
  programTeacher,
  programTopic,
  stripHtml,
  splitProgramDetails,
} from "../../utils/programPricing";

const PAGE_SIZE = 6;

const isSubscriptionPlan = (program) =>
  program?.isSubscription === true ||
  program?.isSubscription === 1 ||
  program?.isSubscription === "1" ||
  program?.isSubscription === "true";

const isCorporateProgram = (program) =>
  program?.isCorporate === true ||
  program?.isCorporate === 1 ||
  program?.isCorporate === "1" ||
  program?.isCorporate === "true";

const isDummyProgram = (program) =>
  /^(test|dummy|sample)(\s*\d+)?$/i.test(String(program?.name || "").trim());

const courseNameKey = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/&amp;/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** Same course when the names match, or one is the other plus extra words ("Vibrant Plate" / "Vibrant Plate Method"). */
const isSameCourseName = (left, right) => {
  const a = courseNameKey(left);
  const b = courseNameKey(right);
  if (!a || !b) return false;
  if (a === b) return true;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  return shorter.length >= 8 && longer.startsWith(`${shorter} `);
};

const courseCtaLabel = ({ purchased, included, isMember, freeForAll }) => {
  if (freeForAll) return "Open";
  if (purchased || (included && isMember)) return "Open";
  if (included && !isMember) return "Become a Member";
  return "Unlock";
};

const Courses = (props) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [entitledCourses, setEntitledCourses] = useState([]);
  const [discountPercent, setDiscountPercent] = useState(20);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });
  const [detailsProgram, setDetailsProgram] = useState(null);

  const isMember = hasActiveMembership(props?.user);
  const accountLoaded = Boolean(props?.user?.accountLoaded);
  const canAccessCourses = hasFeature(
    props?.user,
    MEMBERSHIP_FEATURE_KEYS.COURSES
  );

  const isProgramPurchased = (program) =>
    Array.isArray(props.user?.programPurchased) &&
    props.user.programPurchased.some(
      (purchase) => String(purchase.programSubscriptionId) === String(program.ID)
    );

  useEffect(() => {
    if (!accountLoaded) return;
    if (!canAccessCourses) {
      navigate("/result", { replace: true });
    }
  }, [accountLoaded, canAccessCourses, navigate]);

  useEffect(() => {
    if (!accountLoaded || !canAccessCourses) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getProgramListApi();
        const payload = res?.data?.data || res?.data || {};
        const list = Array.isArray(payload.programList)
          ? payload.programList
          : Array.isArray(payload)
            ? payload
            : [];
        if (cancelled) return;
        setPrograms(list);
        setDiscountPercent(
          getMemberDiscountPercent(payload.memberProgramDiscountPercent)
        );

        try {
          const coursesRes = await getCoursesApi();
          const coursePayload = coursesRes?.data || coursesRes || {};
          const courses =
            coursePayload.courses ||
            coursePayload.data?.courses ||
            [];
          if (!cancelled) {
            setEntitledCourses(Array.isArray(courses) ? courses : []);
          }
        } catch (courseError) {
          if (!cancelled) setEntitledCourses([]);
        }
      } catch (error) {
        if (!cancelled) {
          setPrograms([]);
          setSnack({
            open: true,
            message: error?.message || "Unable to load programs.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [accountLoaded, canAccessCourses, isMember, props?.user?._id]);

  const purchasedIds = props.user?.programPurchased;
  const catalog = useMemo(
    () => {
      const owned = (program) =>
        Array.isArray(purchasedIds) &&
        purchasedIds.some(
          (purchase) => String(purchase.programSubscriptionId) === String(program.ID)
        );
      const sellable = programs
        .filter((program) => {
          if (isSubscriptionPlan(program)) return false;
          if (isCorporateProgram(program)) return false;
          if (isDummyProgram(program)) return false;
          if (program?.showOnVitalityPortal === false && !owned(program)) {
            return false;
          }
          return owned(program) || String(program?.status) === "1";
        })
        .map((program) => ({
          ...program,
          alreadyOwned: owned(program),
        }));

      const entitled = entitledCourses
        .map((course, index) => ({
          ID: `lms-${index}-${course.courseName || "course"}`,
          name: course.courseName,
          description: course.description,
          imageUrl: course.imageUrl,
          url: course.LmsLink,
          LmsLink: course.LmsLink,
          includedWithMembership: true,
          amount: 0,
          isLmsCourse: true,
          wordpressCategoryName: course.Category,
        }))
        .filter((course) => !isDummyProgram(course));

      const includedNames = entitled.map((course) => course.name);
      const sellableForCatalog = sellable.filter(
        (program) =>
          !includedNames.some((name) => isSameCourseName(name, program.name))
      );

      const opensWithMembership = (program) => {
        const included = program.isLmsCourse || isIncludedWithMembership(program);
        if (program.alreadyOwned) return true;
        if (included && isMember) return true;
        if (!isMember && isFreeForNonMembers(program)) return true;
        return false;
      };

      return [...entitled, ...sellableForCatalog].sort((a, b) => {
        const group =
          Number(opensWithMembership(b)) - Number(opensWithMembership(a));
        if (group !== 0) return group;
        return String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
          sensitivity: "base",
        });
      });
    },
    [programs, purchasedIds, entitledCourses, isMember]
  );

  const featured =
    catalog.find(
      (program) => program.isLmsCourse || isIncludedWithMembership(program)
    ) ||
    null;
  // Free badge, "Free for everyone", and direct Open are for non-members only.
  const opensFreeForNonMember = (program) =>
    !isMember && isFreeForNonMembers(program);
  const featuredFreeForAll = opensFreeForNonMember(featured);
  const gridPrograms = catalog.filter((program) => program !== featured);
  const visiblePrograms = gridPrograms.slice(0, visibleCount);
  const memberPct = getMemberDiscountPercent(discountPercent);
  const detailsParts = detailsProgram
    ? splitProgramDetails(detailsProgram.summary || detailsProgram.description, {
        stripImages: true,
        omitTitle: detailsProgram.name,
      })
    : { lead: "", bodyHtml: "" };

  const openCourse = (program) => {
    if (program?.url) {
      window.location.href = program.url;
      return;
    }
    setSnack({
      open: true,
      message: "This program does not have a course link yet.",
      severity: "error",
    });
  };

  const handleFeaturedStart = () => {
    if (!featured) return;
    const included = featured.isLmsCourse || isIncludedWithMembership(featured);
    if (opensFreeForNonMember(featured)) {
      openCourse(featured);
      return;
    }
    if (!isMember && included) {
      navigate("/plans");
      return;
    }
    if (featured.isLmsCourse) {
      if (featured.LmsLink || featured.url) {
        openCourse(featured);
      } else {
        navigate("/plans");
      }
      return;
    }
    if (isMember || isProgramPurchased(featured) || featured.alreadyOwned) {
      openCourse(featured);
      return;
    }
    navigate("/plans");
  };

  const handleUnlock = async (program) => {
    const included = program.isLmsCourse || isIncludedWithMembership(program);
    if (opensFreeForNonMember(program)) {
      openCourse(program);
      return;
    }
    if (included && !isMember && !isProgramPurchased(program) && !program.alreadyOwned) {
      navigate("/plans");
      return;
    }

    if (program.isLmsCourse) {
      if (program.LmsLink || program.url) {
        openCourse(program);
      } else {
        navigate("/plans");
      }
      return;
    }

    if (isIncludedWithMembership(program)) {
      if (isMember || isProgramPurchased(program) || program.alreadyOwned) {
        openCourse(program);
      } else {
        navigate("/plans");
      }
      return;
    }

    if (isProgramPurchased(program) || program.alreadyOwned) {
      openCourse(program);
      return;
    }

    setCheckoutLoading(true);
    try {
      const res = await paymentCheckoutURLApi({
        ID: program.ID,
      });
      const url = res?.data?.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error(res?.data?.message || "Unable to start checkout.");
    } catch (error) {
      setSnack({
        open: true,
        message: error?.message || "Unable to start checkout.",
        severity: "error",
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const renderCard = (program) => {
    const included = program.isLmsCourse || isIncludedWithMembership(program);
    const freeForAll = opensFreeForNonMember(program);
    const purchased = isProgramPurchased(program) || program.alreadyOwned;
    const topic = programTopic(program);
    const teacher = programTeacher(program);
    const description = stripHtml(program.description);
    const nonMember = getNonMemberPrice(program);
    const member = getMemberPrice(program, memberPct);

    return (
      <article className="course-card" key={program.ID}>
        <div className="course-card__media">
          {freeForAll ? (
            <span className="course-card__free">Free</span>
          ) : !isMember && !purchased ? (
            <span className="course-card__lock">Unlock</span>
          ) : null}
          {program.imageUrl ? (
            <img src={program.imageUrl} alt={program.name || "Course image"} />
          ) : (
            <span className="course-card__placeholder">Course image</span>
          )}
        </div>
        <div className="course-card__body">
          {topic ? <p className="course-card__topic">{topic}</p> : null}
          <h3 className="course-card__title">{program.name}</h3>
          {teacher ? (
            <p className="course-card__teacher">with {teacher}</p>
          ) : null}
          {description ? (
            <p className="course-card__desc">{description}</p>
          ) : null}
          {freeForAll ? (
            <p className="course-card__included">Free for everyone</p>
          ) : included && !isMember ? (
            <p className="course-card__included">Included with membership</p>
          ) : !included ? (
            <div className="course-card__prices">
              <div
                className={`course-card__price-row${
                  isMember ? " is-list" : ""
                }`}
              >
                <span>Non-member price</span>
                <strong>{formatProgramPrice(nonMember, program.currency)}</strong>
              </div>
              <div
                className={`course-card__price-row course-card__price-row--member${
                  isMember ? " is-active" : ""
                }`}
              >
                <span>Member price</span>
                <strong>{formatProgramPrice(member, program.currency)}</strong>
              </div>
            </div>
          ) : null}
          <div className="course-card__actions">
            <button
              type="button"
              className="course-card__details"
              onClick={() => setDetailsProgram(program)}
            >
              Details
            </button>
            <button
              type="button"
              className="course-card__cta"
              onClick={() => handleUnlock(program)}
            >
              {courseCtaLabel({ purchased, included, isMember, freeForAll })}
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="courses-page">
      <CenterLoading open={!accountLoaded || loading || checkoutLoading} />
      <section className="courses-hero">
        <div className="courses-hero__inner">
          <p className="courses-kicker">Courses</p>
          <h1>Programs and Courses</h1>
          <p className="courses-hero__lede">
            {featured
              ? featuredFreeForAll
                ? `${featured.name} is free for everyone. Unlock any other program whenever you are ready to go deeper.`
                : `${featured.name} is included with membership. Unlock any other program whenever you are ready to go deeper.`
              : "Unlock any program whenever you are ready to go deeper."}
          </p>
        </div>
        {featured ? (
          <div className="courses-featured">
            <div className="courses-featured__media">
              {featuredFreeForAll ? (
                <span className="course-card__free">Free</span>
              ) : !isMember ? (
                <span className="course-card__lock">Unlock</span>
              ) : null}
              {featured.imageUrl ? (
                <img src={featured.imageUrl} alt={featured.name} />
              ) : (
                <span>Course image</span>
              )}
            </div>
            <div className="courses-featured__copy">
              {featuredFreeForAll ? (
                <p className="courses-featured__badge">Free for everyone</p>
              ) : !isMember ? (
                <p className="courses-featured__badge">
                  <LockOutlinedIcon fontSize="inherit" />
                  Included with membership
                </p>
              ) : null}
              <h2>{featured.name}</h2>
              {programTeacher(featured) ? (
                <p className="courses-featured__teacher">
                  with {programTeacher(featured)}
                </p>
              ) : null}
              {stripHtml(featured.description) ? (
                <p className="courses-featured__desc">
                  {stripHtml(featured.description)}
                </p>
              ) : null}
              <div className="courses-featured__actions">
                <button
                  type="button"
                  className="course-card__details"
                  onClick={() => setDetailsProgram(featured)}
                >
                  Details
                </button>
                <button
                  type="button"
                  className="courses-featured__cta"
                  onClick={handleFeaturedStart}
                >
                  {courseCtaLabel({
                    purchased:
                      isProgramPurchased(featured) || featured.alreadyOwned,
                    included:
                      featured.isLmsCourse || isIncludedWithMembership(featured),
                    isMember,
                    freeForAll: featuredFreeForAll,
                  })}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </section>

      <section className="courses-catalog">
        <div className="courses-catalog__head">
          <div>
            <h2>All programs</h2>
            <p>
              {gridPrograms.length} program{gridPrograms.length === 1 ? "" : "s"}, A to Z
            </p>
          </div>
        </div>

        <div className="courses-pricing-strip">
          <p>
            Member pricing. Members save {memberPct}% on every program.
          </p>
          {!isMember ? (
            <button type="button" onClick={() => navigate("/plans")}>
              Become a member
            </button>
          ) : null}
        </div>

        {visiblePrograms.length > 0 ? (
          <div className="courses-grid">{visiblePrograms.map(renderCard)}</div>
        ) : (
          <p className="courses-empty">
            No programs are available yet. Check back soon.
          </p>
        )}

        {visibleCount < gridPrograms.length ? (
          <div className="courses-more">
            <button
              type="button"
              onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
            >
              Show more programs
            </button>
          </div>
        ) : null}
      </section>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        handleCloseSnackBar={() => setSnack((prev) => ({ ...prev, open: false }))}
      />
      <Modal
        open={Boolean(detailsProgram)}
        onClose={() => setDetailsProgram(null)}
      >
        <Box className="course-details-modal">
          <button
            type="button"
            className="course-details-modal__close"
            onClick={() => setDetailsProgram(null)}
            aria-label="Close"
          >
            <img src={closex} alt="" />
          </button>
          <p className="course-details-modal__kicker">Course details</p>
          <h2>{detailsProgram?.name}</h2>
          {detailsParts.lead ? (
            <p className="course-details-modal__desc">{detailsParts.lead}</p>
          ) : null}
          {programTeacher(detailsProgram) ? (
            <p className="course-details-modal__teacher">
              with {programTeacher(detailsProgram)}
            </p>
          ) : null}
          {detailsParts.bodyHtml ? (
            <div className="course-details-modal__body">
              <div
                className="course-details-modal__html"
                dangerouslySetInnerHTML={{ __html: detailsParts.bodyHtml }}
              />
            </div>
          ) : null}
        </Box>
      </Modal>
    </div>
  );
};

export default connect(({ user }) => ({ user }))(Courses);
